import workletUrl from './audio-worklet.js?url';

export interface RecordedClip {
  samples: Float32Array;   // mono, Float32 [-1, 1]
  sampleRate: number;
  channels: 1;
  durationSec: number;
}

export class Recorder {
  private ctx: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private stream: MediaStream | null = null;
  private analyser: AnalyserNode | null = null;
  private buffer: Float32Array[] = [];
  private capturing = false;
  private deviceId?: string;

  async init(deviceId?: string) {
    if (this.ctx) return;
    this.deviceId = deviceId;
    this.ctx = new AudioContext();
    await this.ctx.audioWorklet.addModule(workletUrl);

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        channelCount: 1,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
      video: false,
    });

    this.source = this.ctx.createMediaStreamSource(this.stream);
    this.node = new AudioWorkletNode(this.ctx, 'capture-processor');
    this.node.port.onmessage = (e: MessageEvent<Float32Array>) => {
      if (this.capturing) this.buffer.push(e.data);
    };
    this.source.connect(this.node);

    // Tee the source into an AnalyserNode for the live recording monitor
    // (level meter + RPM detector + intensity trace). The analyser is
    // independent of the capture path — the monitor reads it on its own
    // schedule without affecting recording fidelity. fftSize=32768 gives
    // ~1.5 Hz resolution at 48 kHz, which after parabolic refinement and
    // firing-rate division (typically ÷3 for V6) yields ~5–10 RPM
    // resolution — fine for a live "this is working" indicator.
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 32768;
    this.analyser.smoothingTimeConstant = 0.0;
    this.source.connect(this.analyser);
    // Worklet is kept alive by the source connection; no destination wiring
    // (we don't want to play back to the speakers).
  }

  /** AnalyserNode tee'd off the capture source. Lives for the duration
   *  of init()..dispose(). Caller polls it via getFloatTimeDomainData
   *  (level meter) and getFloatFrequencyData (RPM detector). Null until
   *  init() has resolved. */
  get monitorAnalyser(): AnalyserNode | null {
    return this.analyser;
  }

  get sampleRate(): number {
    if (!this.ctx) throw new Error('Recorder not initialised');
    return this.ctx.sampleRate;
  }

  /** Returns the deviceId and label that the OS actually attached the
   *  MediaStream to. On Android with Android Auto active these can differ
   *  from what was requested, so callers should compare and surface a
   *  warning if they don't match. */
  get activeStreamInfo(): { deviceId: string | null; label: string } | null {
    const track = this.stream?.getAudioTracks()[0];
    if (!track) return null;
    const settings = track.getSettings();
    return {
      deviceId: typeof settings.deviceId === 'string' ? settings.deviceId : null,
      // Track labels are only populated after mic permission is granted.
      label: track.label || '',
    };
  }

  /** The deviceId the caller asked for via init(); useful for comparing
   *  against activeStreamInfo.deviceId. */
  get requestedDeviceId(): string | undefined {
    return this.deviceId;
  }

  /** Captures audio for `durationSec` and returns the merged Float32 buffer. */
  async recordClip(durationSec: number): Promise<RecordedClip> {
    if (!this.ctx || !this.node) throw new Error('Recorder not initialised');
    this.buffer = [];
    this.capturing = true;
    await sleep(durationSec * 1000);
    this.capturing = false;

    const total = this.buffer.reduce((n, b) => n + b.length, 0);
    const merged = new Float32Array(total);
    let off = 0;
    for (const b of this.buffer) { merged.set(b, off); off += b.length; }
    this.buffer = [];

    return {
      samples: merged,
      sampleRate: this.ctx.sampleRate,
      channels: 1,
      durationSec: total / this.ctx.sampleRate,
    };
  }

  async dispose() {
    this.capturing = false;
    try { this.node?.port.postMessage({ type: 'stop' }); } catch { /* ignore */ }
    this.node?.disconnect();
    this.analyser?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    if (this.ctx && this.ctx.state !== 'closed') {
      try { await this.ctx.close(); } catch { /* ignore */ }
    }
    this.ctx = null;
    this.node = null;
    this.analyser = null;
    this.source = null;
    this.stream = null;
    this.buffer = [];
  }
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function listAudioInputDevices(): Promise<MediaDeviceInfo[]> {
  // Labels are only populated after permission is granted; callers may need
  // to call getUserMedia once before this.
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d) => d.kind === 'audioinput');
}
